import { useCallback, useEffect, useState } from 'react';
import axios, { AxiosResponse } from 'axios';

type RemoteMethod<T, P> = ((payload: P) => Promise<AxiosResponse<T> | T>) | undefined;

export const useRemotePost = <T, P>({
	remoteMethod,
	lazy = true // Default a true, per chiamare manualmente con fetch()
}: {
	remoteMethod: RemoteMethod<T, P>;
	lazy?: boolean;
}) => {
	const [data, setData] = useState<T | undefined>(undefined);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | undefined>(undefined);
	const [errorDetail, setErrorDetail] = useState<string | undefined>(undefined);
	const [errorCode, setErrorCode] = useState<number | undefined>(undefined);

	// Metodo per resettare l'errore
	const resetError = () => {
		setError(undefined);
		setErrorDetail(undefined);
		setErrorCode(undefined);
	};

	// Metodo per resettare lo stato
	const resetState = () => {
		setData(undefined);
		setError(undefined);
		setErrorDetail(undefined);
		setErrorCode(undefined);
	};

	const postData = useCallback(
		async (payload: P) => {
			if (!remoteMethod) {
				setError('Nessun metodo remoto definito');
				return;
			}

			try {
				setLoading(true);
				const response = await remoteMethod(payload);
				if (response && typeof response === 'object' && 'data' in response) {
					// È un AxiosResponse
					setData(response.data);
				} else {
					// È direttamente il dato
					setData(response as T);
				}
				setError(undefined);
				setErrorDetail(undefined);
				setErrorCode(undefined);
			} catch (err: any) {
				let T_error = 'Errore sconosciuto';
				let T_errorDetail = 'Si è verificato un problema imprevisto';
				let T_errorCode: number | undefined = undefined;

				if (axios.isAxiosError(err)) {
					if (err.response) {
						// Il server ha risposto con un codice di errore (4xx, 5xx)
						T_errorCode = err.response.status;

						// Gestione specifica per il 413
						if (T_errorCode === 413) {
							T_error = 'Caricamento fallito';
							T_errorDetail = 'Il file è troppo grande e supera il limite consentito.';
						} else {
							// Prova a prendere il messaggio di errore dal body della risposta
							T_error = err.response.data?.title || err.response.statusText || 'Errore dal server';
							T_errorDetail =
								err.response.data?.detail || err.response.data?.message || 'Il server ha risposto con un errore.';
						}
					} else if (err.request) {
						// La richiesta è partita ma non è stata ricevuta risposta 'ERR_NETWORK'
						T_error = 'Errore di Rete';
						T_errorDetail = 'Impossibile contattare il server. Controlla la tua connessione.';
					} else {
						// Errore nella configurazione di Axios
						T_error = 'Errore di Configurazione';
						T_errorDetail = err.message;
					}
				} else {
					// Errore JavaScript generico OPPURE un oggetto JHipster 'problem'
					T_error = err.body.title || err.body.message || "Errore durante l'invio dei dati";
					T_errorDetail = err.body.detail || err.body.message || 'Dettaglio non disponibile';
					T_errorCode = err.body.status || undefined;
				}

				setError(T_error);
				setErrorDetail(T_errorDetail);
				setErrorCode(T_errorCode);
			} finally {
				setLoading(false);
			}
		},
		[remoteMethod]
	);

	useEffect(() => {
		if (!lazy && remoteMethod) {
			// Se lazy=false ed esiste un metodo remoto, chiama automaticamente postData con un payload vuoto
			postData({} as P);
		}
	}, [lazy, remoteMethod, postData]);

	return {
		data,
		loading,
		error,
		errorDetail,
		errorCode,
		fetch: postData,
		resetError,
		resetState
	};
};
